/**
 * Number of items the "Docs in chat" drawer lists for the thread currently in
 * `messageSlice.threadData`: generated documents (user uploads excluded, they
 * are counted once as attachments), the legacy Google Doc if the thread has
 * one, and unique uploaded attachments.
 *
 * Used by DocsInChatToggle, which both the chat top bar and the shared-page
 * header render, so the two surfaces always show the same count for the same
 * conversation. Keep this in step with the lists DocsInChatDrawer builds.
 */
import { useMemo } from "react";
import { MESSAGE_TYPES } from "src/domain/messages";
import { useAppSelector } from "src/hooks/useAppSelector";

type ThreadDocument = { meta?: { is_user_uploaded?: boolean } } | null | undefined;
type ThreadMessage = {
  message_type?: string;
  payload?: { attachments?: Array<{ reference_doc_storage_id?: string | null }> | null } | null;
};

const useDocsInChatCount = (): number => {
  const threadDocuments = useAppSelector(
    (state) => (state.messageSlice as any).threadData?.documents as Record<string, ThreadDocument> | undefined,
  );
  const legacyDoc = useAppSelector((state) => (state.messageSlice as any).threadData?.document);
  const messages = useAppSelector(
    (state) => (state.messageSlice as any).threadData?.messages as ThreadMessage[] | undefined,
  );

  const docCount = Object.values(threadDocuments || {}).filter((d) => !d?.meta?.is_user_uploaded).length;

  const attachmentCount = useMemo(() => {
    const seen = new Set<string>();
    (messages || []).forEach((msg) => {
      if (msg?.message_type === MESSAGE_TYPES.ATTACHMENT && msg?.payload?.attachments) {
        msg.payload.attachments.forEach((att) => {
          const id = att.reference_doc_storage_id;
          if (id && !seen.has(id)) seen.add(id);
        });
      }
    });
    return seen.size;
  }, [messages]);

  return docCount + (legacyDoc?.google_doc_id ? 1 : 0) + attachmentCount;
};

export default useDocsInChatCount;
